import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';

import { Button } from 'common/components/Button';
import { DeleteButton } from 'common/components/Button/DeleteButton';
import { IOfferEventDetailFragment } from 'graphql/types';
import { OfferEventUnsubscribeDialog } from 'offer/components/OfferEventUnsubscribeDialog';
import { useGroupConditionRows } from 'offer/hooks/useGroupConditionRows';

import { EnrollParticipantsDrawer } from './EnrollParticipantsDrawer';
import { GroupConditionEnrollments } from './GroupConditionEnrollments';

interface IProps {
    canManageParticipants?: boolean;
    offerEvent: IOfferEventDetailFragment;
    unenrollClosed?: boolean;
    onSubmit?: () => void;
}

export const UnsubscribeSection = ({
    canManageParticipants,
    offerEvent,
    unenrollClosed,
    onSubmit,
}: IProps) => {
    const [translate] = useTranslation();
    const [dialogOpen, setDialogOpen] = useState(false);
    const [isUserSelectorOpen, setIsUserSelectorOpen] = useState(false);

    const {
        rows: groupConditionRows,
        loading: groupConditionRowsLoading,
        canEnrollParticipantsAsGroupManager,
    } = useGroupConditionRows({
        canManageParticipants,
        offerEvent,
    });

    // Hide both enroll variants until membership resolves so we don't flash
    // the general enroll button for a manager who actually has group-scoped
    // rows.
    const showGroupConditionRows =
        !groupConditionRowsLoading && !!groupConditionRows.length;
    const showGeneralEnrollButton =
        !groupConditionRowsLoading &&
        canEnrollParticipantsAsGroupManager &&
        !showGroupConditionRows;

    // Reset the user-selector state when the drawer is no longer reachable so
    // it doesn't reappear pre-opened if the conditions flip back.
    useEffect(() => {
        if (!showGeneralEnrollButton) {
            setIsUserSelectorOpen(false);
        }
    }, [showGeneralEnrollButton]);

    return (
        <Stack spacing={2}>
            {showGroupConditionRows && (
                <GroupConditionEnrollments
                    showBottomDivider
                    groupConditionRows={groupConditionRows}
                    offerEvent={offerEvent}
                />
            )}

            <Box
                sx={{
                    alignItems: 'center',
                    display: 'flex',
                    gap: 2,
                }}
            >
                {showGeneralEnrollButton && (
                    <Button
                        color="primary"
                        variant="contained"
                        onClick={() => setIsUserSelectorOpen(true)}
                    >
                        {translate('offerEventDetail.enrollParticipants')}
                    </Button>
                )}

                {unenrollClosed ? (
                    <Typography
                        role="status"
                        sx={{ ml: 'auto', mt: 1, color: 'grey.600' }}
                        variant="body2"
                    >
                        {translate('offerEventDetail.unenrollClosedMessage')}
                    </Typography>
                ) : (
                    <DeleteButton
                        aria-label={translate('unregister')}
                        sx={{ ml: 'auto' }}
                        onClick={() => setDialogOpen(true)}
                    >
                        {translate('unregister')}
                    </DeleteButton>
                )}

                {!unenrollClosed && (
                    <OfferEventUnsubscribeDialog
                        offerEvent={offerEvent}
                        open={dialogOpen}
                        onCancel={() => setDialogOpen(false)}
                        onClose={() => {
                            setDialogOpen(false);
                            onSubmit?.();
                        }}
                    />
                )}

                {showGeneralEnrollButton && (
                    <EnrollParticipantsDrawer
                        offerEvent={offerEvent}
                        open={isUserSelectorOpen}
                        onOpenChange={setIsUserSelectorOpen}
                    />
                )}
            </Box>
        </Stack>
    );
};
